import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { api, relTime } from '../lib/api'

/**
 * Rebound — the bounce & complaint desk. Every rejected or complained-about recipient with the
 * remote server's own SMTP code and words. Hard bounces and complaints are auto-suppressed and
 * transient failures auto-retried (3x); this is the window onto that, plus a link to resend.
 */
export function Rebound() {
  const [kind, setKind] = useState('')
  const { data, isLoading } = useQuery({ queryKey: ['bounces', kind], queryFn: () => api.bounces.list(kind) })
  const s = data?.summary
  const rows = data?.data ?? []

  return (
    <div>
      <h1>Rebound</h1>
      <p className="lede">
        Bounces and complaints with the receiving server's own diagnostics. Hard bounces and
        complaints are auto-suppressed; transient failures auto-retry up to 3 times.
      </p>

      <div className="stats" style={{ marginBottom: 20 }}>
        <div className="rate ok"><div className="rate-label">Delivered (30d)</div><div className="rate-value">{(s?.delivered ?? 0).toLocaleString()}</div></div>
        <div className="rate warn"><div className="rate-label">Hard bounces</div><div className="rate-value">{s?.bounced_hard ?? 0}</div><div className="rate-hint">permanent — auto-suppressed</div></div>
        <div className="rate"><div className="rate-label">Soft bounces</div><div className="rate-value">{s?.bounced_soft ?? 0}</div><div className="rate-hint">transient — retried</div></div>
        <div className="rate critical"><div className="rate-label">Complaints</div><div className="rate-value">{s?.complained ?? 0}</div><div className="rate-hint">auto-suppressed</div></div>
      </div>

      <div className="toolbar" style={{ marginBottom: 12 }}>
        <div className="segmented">
          {([['', 'All'], ['bounce', 'Bounces'], ['complaint', 'Complaints']] as const).map(([v, label]) => (
            <button key={v} className={kind === v ? 'active' : ''} onClick={() => setKind(v)}>{label}</button>
          ))}
        </div>
      </div>

      {isLoading && <div className="skeleton" style={{ height: 200 }} />}
      {!isLoading && rows.length === 0 && (
        <div className="empty"><div className="big">✓</div><p><strong>No bounces or complaints</strong></p><p>Nothing has been rejected recently. That's the goal.</p></div>
      )}

      {rows.length > 0 && (
        <div className="card scroll-x">
          <table className="data">
            <thead><tr><th>Recipient</th><th>Status</th><th>Code</th><th>Remote response</th><th>Message</th><th>When</th></tr></thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={i}>
                  <td className="mono small">{r.address}<div className="muted">{r.remote_mx}</div></td>
                  <td>{r.status === 'complained' ? <span className="badge critical">complaint</span> : <span className="badge failed">bounce</span>}</td>
                  <td className="mono small">{r.smtp_code ?? '—'}{r.dsn ? ` / ${r.dsn}` : ''}</td>
                  <td className="small muted" style={{ maxWidth: 340 }}>{r.response ?? '—'}</td>
                  <td className="small"><Link to={`/messages/${r.message_id}`}>{r.subject || r.message_id}</Link></td>
                  <td className="small muted">{relTime(r.at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
